"use client";

/**
 * Bridges the live BB theme → a Stripe Elements `appearance` object so the
 * embedded Payment Element matches whatever theme the user is currently in.
 *
 * Our theme colors live in CSS custom properties as HSL *triplets* (e.g.
 * `--primary: 142 70% 45%`), the shadcn convention. Stripe's appearance API
 * wants real CSS color strings, and its validator is happiest with hex — so we
 * read the computed custom properties and convert each triplet to #rrggbb.
 *
 * Call this on the client right before mounting <Elements>. It reads from
 * document.documentElement, so it reflects the active theme (including any
 * runtime theme switch) at mount time.
 */
import type { Appearance } from "@stripe/stripe-js";

function readVar(name: string): string {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** "142 70% 45%" → "#rrggbb" (or null if the value isn't an HSL triplet). */
function hslTripletToHex(triplet: string): string | null {
  const m = triplet.match(/^(-?[\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!m) return null;
  const h = ((parseFloat(m[1]) % 360) + 360) % 360;
  const s = Math.min(Math.max(parseFloat(m[2]) / 100, 0), 1);
  const l = Math.min(Math.max(parseFloat(m[3]) / 100, 0), 1);
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const mm = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  const toHex = (v: number) => Math.round((v + mm) * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function varHex(name: string, fallback: string): string {
  return hslTripletToHex(readVar(name)) || fallback;
}

/** Perceived luminance of a #rrggbb color, 0 (black) → 1 (white). */
function luminance(hex: string): number {
  const m = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return 0.5;
  const [r, g, b] = [1, 2, 3].map((i) => parseInt(m[i], 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function buildStripeAppearance(): Appearance {
  // The Payment Element renders on top of the dialog surface (--card).
  const surface = varHex("--card", "#0a0a0a");
  const inputBg = varHex("--background", surface);
  const text = varHex("--foreground", "#fafafa");
  const primary = varHex("--primary", "#22c55e");
  const border = varHex("--border", "#3f3f46");
  const muted = varHex("--muted-foreground", "#a1a1aa");

  const radiusRem = parseFloat(readVar("--radius")) || 0.5;
  const borderRadius = `${Math.round(radiusRem * 16)}px`;

  const fontFamily =
    readVar("--font-sans") ||
    (typeof window !== "undefined" ? getComputedStyle(document.body).fontFamily : "") ||
    "system-ui, -apple-system, sans-serif";

  const dark = luminance(surface) < 0.5;

  return {
    // 'night' gives readable defaults on dark surfaces, 'stripe' on light ones;
    // our variables then override the actual colors to the live theme.
    theme: dark ? "night" : "stripe",
    labels: "floating",
    variables: {
      colorPrimary: primary,
      colorBackground: surface,
      colorText: text,
      colorTextSecondary: muted,
      colorTextPlaceholder: muted,
      colorDanger: "#ef4444",
      fontFamily,
      borderRadius,
      spacingUnit: "4px",
    },
    rules: {
      ".Input": { backgroundColor: inputBg, borderColor: border, color: text },
      ".Input:focus": { borderColor: primary, boxShadow: `0 0 0 1px ${primary}` },
      ".Input::placeholder": { color: muted },
      ".Tab": { backgroundColor: inputBg, borderColor: border },
      ".Tab:hover": { borderColor: primary },
      ".Tab--selected": { borderColor: primary, color: primary },
      ".Label": { color: muted },
      ".Block": { backgroundColor: inputBg, borderColor: border },
    },
  };
}
